'use client';

/**
 * System Design Canvas
 * Architecture diagramming tool for system design
 */

import { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import ToolSEO from '@/components/ToolSEO';
import {
  SystemDesignDiagram,
  SystemComponent,
  SystemConnection,
  ComponentType,
  COMPONENT_TYPE_CONFIG,
  DEFAULT_DIAGRAM_SETTINGS,
} from '@/lib/system-design/types';
import * as storage from '@/lib/system-design/storage';
import { DIAGRAM_TEMPLATES } from '@/lib/system-design/templates';
import { ComponentPalette } from '@/components/system-design/ComponentPalette';
import { ComponentInspector } from '@/components/system-design/ComponentInspector';

// Dynamic import for canvas (no SSR)
const SystemDesignCanvas = dynamic(
  () => import('@/components/system-design/SystemDesignCanvas'),
  { ssr: false, loading: () => <div className="flex-1 bg-background flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" /></div> }
);

// Tier access levels
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };

type UserTier = 'free' | 'pro' | 'team';

function getFeatureAccess(tier: UserTier) {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    canEdit: tierLevel >= TIER_HIERARCHY.free,
    canSave: tierLevel >= TIER_HIERARCHY.free,
    maxDiagrams: tier === 'free' ? 3 : Infinity,
    maxComponents: tier === 'free' ? 20 : Infinity,
    canExportSvg: tierLevel >= TIER_HIERARCHY.pro,
    canExportMarkdown: tierLevel >= TIER_HIERARCHY.pro,
    canEstimateCosts: tierLevel >= TIER_HIERARCHY.pro,
    canUseTechSuggestions: tierLevel >= TIER_HIERARCHY.pro,
    canUseTemplates: tierLevel >= TIER_HIERARCHY.pro,
  };
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export default function SystemDesignPage() {
  const [userTier] = useState<UserTier>('pro'); // TODO: Get from auth
  const [diagrams, setDiagrams] = useState<SystemDesignDiagram[]>([]);
  const [currentDiagram, setCurrentDiagram] = useState<SystemDesignDiagram | null>(null);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialogs
  const [showNewDiagram, setShowNewDiagram] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [newDiagramName, setNewDiagramName] = useState('');

  const features = getFeatureAccess(userTier);

  // Load diagrams
  useEffect(() => {
    async function load() {
      try {
        const list = await storage.listDiagrams();
        setDiagrams(list);
        if (list.length > 0) {
          setCurrentDiagram(list[0]);
        }
      } catch (err) {
        console.error('[SystemDesign] Failed to load diagrams:', err);
        setError('Failed to load diagrams');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Auto-save on changes
  const saveDiagram = useCallback(async (diagram: SystemDesignDiagram) => {
    if (!features.canSave) return;

    setSaving(true);
    try {
      // Calculate total cost
      const totalCost = storage.calculateTotalCost(diagram);
      const updatedDiagram = { ...diagram, totalMonthlyCost: totalCost };

      await storage.saveDiagram(updatedDiagram);
      setDiagrams((prev) =>
        prev.map((d) => (d.id === diagram.id ? updatedDiagram : d))
      );
    } catch (err) {
      console.error('[SystemDesign] Failed to save:', err);
      setError('Failed to save diagram');
    } finally {
      setSaving(false);
    }
  }, [features.canSave]);

  // Selected items
  const selectedComponent = currentDiagram?.components.find((c) => c.id === selectedComponentId) || null;
  const selectedConnection = currentDiagram?.connections.find((c) => c.id === selectedConnectionId) || null;

  // Handlers
  const handleCreateDiagram = async () => {
    if (!newDiagramName.trim()) return;

    if (diagrams.length >= features.maxDiagrams) {
      setError(`Free tier limited to ${features.maxDiagrams} diagrams. Upgrade to Pro for more.`);
      return;
    }

    try {
      const diagram = await storage.createDiagram(newDiagramName.trim());
      setDiagrams((prev) => [diagram, ...prev]);
      setCurrentDiagram(diagram);
      setShowNewDiagram(false);
      setNewDiagramName('');
    } catch (err) {
      setError('Failed to create diagram');
    }
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    const template = DIAGRAM_TEMPLATES.find((t) => t.id === templateId);
    if (!template) return;

    if (diagrams.length >= features.maxDiagrams) {
      setError(`Free tier limited to ${features.maxDiagrams} diagrams. Upgrade to Pro for more.`);
      return;
    }

    try {
      const templateDiagram = template.createDiagram();
      const now = new Date().toISOString();
      const diagram: SystemDesignDiagram = {
        ...templateDiagram,
        id: generateId(),
        createdAt: now,
        updatedAt: now,
      };

      await storage.saveDiagram(diagram);
      setDiagrams((prev) => [diagram, ...prev]);
      setCurrentDiagram(diagram);
      setShowTemplates(false);
    } catch (err) {
      setError('Failed to create diagram from template');
    }
  };

  const handleDeleteDiagram = async (id: string) => {
    if (!confirm('Delete this diagram? This cannot be undone.')) return;

    try {
      await storage.deleteDiagram(id);
      setDiagrams((prev) => prev.filter((d) => d.id !== id));
      if (currentDiagram?.id === id) {
        setCurrentDiagram(diagrams.find((d) => d.id !== id) || null);
        setSelectedComponentId(null);
        setSelectedConnectionId(null);
      }
    } catch (err) {
      setError('Failed to delete diagram');
    }
  };

  const handleAddComponent = (type: ComponentType) => {
    if (!currentDiagram) return;

    if (currentDiagram.components.length >= features.maxComponents) {
      setError(`Free tier limited to ${features.maxComponents} components. Upgrade to Pro for more.`);
      return;
    }

    const config = COMPONENT_TYPE_CONFIG[type];
    const newComponent: SystemComponent = {
      id: generateId(),
      type,
      name: config.label,
      position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
      size: config.defaultSize,
    };

    const updated = {
      ...currentDiagram,
      components: [...currentDiagram.components, newComponent],
    };

    setCurrentDiagram(updated);
    setSelectedComponentId(newComponent.id);
    setSelectedConnectionId(null);
    saveDiagram(updated);
  };

  const handleMoveComponent = (id: string, x: number, y: number) => {
    if (!currentDiagram) return;

    const updated = {
      ...currentDiagram,
      components: currentDiagram.components.map((c) =>
        c.id === id ? { ...c, position: { x, y } } : c
      ),
    };

    setCurrentDiagram(updated);
    // Debounce save
    saveDiagram(updated);
  };

  const handleUpdateComponent = (component: SystemComponent) => {
    if (!currentDiagram) return;

    const updated = {
      ...currentDiagram,
      components: currentDiagram.components.map((c) =>
        c.id === component.id ? component : c
      ),
    };

    setCurrentDiagram(updated);
    saveDiagram(updated);
  };

  const handleDeleteComponent = (id: string) => {
    if (!currentDiagram) return;

    const updated = {
      ...currentDiagram,
      components: currentDiagram.components.filter((c) => c.id !== id),
      connections: currentDiagram.connections.filter(
        (c) => c.sourceId !== id && c.targetId !== id
      ),
    };

    setCurrentDiagram(updated);
    setSelectedComponentId(null);
    saveDiagram(updated);
  };

  const handleCreateConnection = (sourceId: string, targetId: string) => {
    if (!currentDiagram) return;

    // Check if connection already exists
    const exists = currentDiagram.connections.some(
      (c) =>
        (c.sourceId === sourceId && c.targetId === targetId) ||
        (c.sourceId === targetId && c.targetId === sourceId)
    );
    if (exists) return;

    const newConnection: SystemConnection = {
      id: generateId(),
      sourceId,
      targetId,
      connectionType: 'http',
      dataFlow: 'bidirectional',
    };

    const updated = {
      ...currentDiagram,
      connections: [...currentDiagram.connections, newConnection],
    };

    setCurrentDiagram(updated);
    setSelectedConnectionId(newConnection.id);
    setSelectedComponentId(null);
    saveDiagram(updated);
  };

  const handleUpdateConnection = (connection: SystemConnection) => {
    if (!currentDiagram) return;

    const updated = {
      ...currentDiagram,
      connections: currentDiagram.connections.map((c) =>
        c.id === connection.id ? connection : c
      ),
    };

    setCurrentDiagram(updated);
    saveDiagram(updated);
  };

  const handleDeleteConnection = (id: string) => {
    if (!currentDiagram) return;

    const updated = {
      ...currentDiagram,
      connections: currentDiagram.connections.filter((c) => c.id !== id),
    };

    setCurrentDiagram(updated);
    setSelectedConnectionId(null);
    saveDiagram(updated);
  };

  const handleExport = (format: 'png' | 'svg' | 'json') => {
    if (!currentDiagram) return;

    if (format === 'json') {
      const json = storage.exportToJSON(currentDiagram);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentDiagram.name.toLowerCase().replace(/\s+/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (format === 'png' || format === 'svg') {
      // Export SVG from canvas
      const svg = document.querySelector('.system-design-canvas svg');
      if (!svg) return;

      const svgData = new XMLSerializer().serializeToString(svg);

      if (format === 'svg') {
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${currentDiagram.name.toLowerCase().replace(/\s+/g, '-')}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // PNG export via canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          const pngUrl = canvas.toDataURL('image/png');
          const a = document.createElement('a');
          a.href = pngUrl;
          a.download = `${currentDiagram.name.toLowerCase().replace(/\s+/g, '-')}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      }
    }

    setShowExport(false);
  };

  return (
    <>
      <ToolSEO
        toolName="System Design Canvas"
        toolSlug="system-design"
        description="Architecture diagramming tool for system design. Drag-and-drop components, create connections, and export to PNG/SVG."
        category="planner"
        isPremium={true}
      />

      <main className="min-h-screen bg-background text-foreground">
        <div className="flex flex-col h-screen">
          {/* Header */}
          <header className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold">System Design Canvas</h1>

              {/* Diagram selector */}
              {diagrams.length > 0 && (
                <select
                  value={currentDiagram?.id || ''}
                  onChange={(e) => {
                    const diagram = diagrams.find((d) => d.id === e.target.value);
                    setCurrentDiagram(diagram || null);
                    setSelectedComponentId(null);
                    setSelectedConnectionId(null);
                  }}
                  className="px-3 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
                >
                  {diagrams.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              )}

              {currentDiagram && (
                <>
                  <input
                    type="text"
                    value={currentDiagram.name}
                    onChange={(e) => {
                      const updated = { ...currentDiagram, name: e.target.value };
                      setCurrentDiagram(updated);
                      saveDiagram(updated);
                    }}
                    className="px-3 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent w-48"
                  />
                  <button
                    onClick={() => handleDeleteDiagram(currentDiagram.id)}
                    className="p-1.5 text-foreground-tertiary hover:text-red-400 transition-colors"
                    title="Delete diagram"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </>
              )}

              {saving && (
                <span className="text-sm text-foreground-tertiary">Saving...</span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Tier badge */}
              <span className={`
                px-2 py-1 rounded text-xs font-medium
                ${userTier === 'team' ? 'bg-purple-500/20 text-purple-400' :
                  userTier === 'pro' ? 'bg-accent-muted text-accent' :
                  'bg-cream/10 text-foreground-tertiary'}
              `}>
                {userTier.toUpperCase()}
              </span>

              {/* Cost display */}
              {features.canEstimateCosts && currentDiagram?.totalMonthlyCost !== undefined && currentDiagram.totalMonthlyCost > 0 && (
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                  ~${currentDiagram.totalMonthlyCost.toFixed(0)}/mo
                </span>
              )}

              {features.canUseTemplates && (
                <button
                  onClick={() => setShowTemplates(true)}
                  className="px-3 py-1.5 text-sm text-foreground-secondary hover:text-foreground transition-colors"
                >
                  Templates
                </button>
              )}

              {currentDiagram && (
                <button
                  onClick={() => setShowExport(true)}
                  className="px-3 py-1.5 bg-cream/10 text-foreground rounded text-sm hover:bg-cream/20 transition-colors"
                >
                  Export
                </button>
              )}

              <button
                onClick={() => setShowNewDiagram(true)}
                className="px-3 py-1.5 bg-accent text-accent-foreground rounded text-sm font-medium hover:bg-accent-hover transition-colors"
              >
                + New
              </button>
            </div>
          </header>

          {/* Error message */}
          {error && (
            <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/30 text-red-400 text-sm flex items-center justify-between">
              {error}
              <button onClick={() => setError(null)} className="underline">Dismiss</button>
            </div>
          )}

          {/* Main content */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-foreground-tertiary">Loading diagrams...</p>
              </div>
            </div>
          ) : diagrams.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-cream/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-foreground-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold mb-2">No Diagrams</h2>
                <p className="text-foreground-tertiary mb-6">Create your first system design diagram</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowNewDiagram(true)}
                    className="px-6 py-3 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover"
                  >
                    Create Blank Diagram
                  </button>
                  {features.canUseTemplates && (
                    <button
                      onClick={() => setShowTemplates(true)}
                      className="px-6 py-3 bg-cream/10 text-foreground rounded hover:bg-cream/20"
                    >
                      Use Template
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : currentDiagram ? (
            <div className="flex-1 flex overflow-hidden">
              {/* Left sidebar - Component Palette */}
              <ComponentPalette
                onAddComponent={handleAddComponent}
                canEdit={features.canEdit}
              />

              {/* Canvas */}
              <div className="flex-1 system-design-canvas overflow-hidden">
                <SystemDesignCanvas
                  components={currentDiagram.components}
                  connections={currentDiagram.connections}
                  settings={currentDiagram.settings}
                  selectedComponentId={selectedComponentId || undefined}
                  selectedConnectionId={selectedConnectionId || undefined}
                  onSelectComponent={setSelectedComponentId}
                  onSelectConnection={setSelectedConnectionId}
                  onMoveComponent={handleMoveComponent}
                  onCreateConnection={handleCreateConnection}
                  canEdit={features.canEdit}
                />
              </div>

              {/* Right sidebar - Inspector */}
              <ComponentInspector
                selectedComponent={selectedComponent}
                selectedConnection={selectedConnection}
                onUpdateComponent={handleUpdateComponent}
                onUpdateConnection={handleUpdateConnection}
                onDeleteComponent={handleDeleteComponent}
                onDeleteConnection={handleDeleteConnection}
                canEdit={features.canEdit}
                canEstimateCosts={features.canEstimateCosts}
              />
            </div>
          ) : null}
        </div>

        {/* New Diagram Dialog */}
        {showNewDiagram && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold mb-4">New Diagram</h3>

              <div>
                <label className="block text-sm text-foreground-secondary mb-1">Name</label>
                <input
                  type="text"
                  value={newDiagramName}
                  onChange={(e) => setNewDiagramName(e.target.value)}
                  placeholder="e.g., E-commerce Architecture"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                  autoFocus
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowNewDiagram(false);
                    setNewDiagramName('');
                  }}
                  className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDiagram}
                  disabled={!newDiagramName.trim()}
                  className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium disabled:opacity-50"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Templates Dialog */}
        {showTemplates && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background border border-border rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="text-xl font-semibold">Templates</h3>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="p-2 text-foreground-tertiary hover:text-foreground"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-2 gap-4">
                  {DIAGRAM_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleCreateFromTemplate(template.id)}
                      className="p-4 text-left border border-border rounded-lg hover:border-border-muted0 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{template.icon}</span>
                        <span className="font-medium text-foreground">{template.name}</span>
                      </div>
                      <p className="text-sm text-foreground-tertiary">{template.description}</p>
                      <span className="inline-block mt-2 px-2 py-0.5 bg-cream/10 text-foreground-tertiary text-xs rounded">
                        {template.category}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Dialog */}
        {showExport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold mb-4">Export Diagram</h3>

              <div className="space-y-3">
                <button
                  onClick={() => handleExport('png')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:border-border-muted0 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded flex items-center justify-center">
                      <span className="text-blue-400 font-bold text-sm">PNG</span>
                    </div>
                    <div>
                      <p className="font-medium">PNG Image</p>
                      <p className="text-sm text-foreground-tertiary">Raster image for presentations</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => features.canExportSvg ? handleExport('svg') : null}
                  disabled={!features.canExportSvg}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    features.canExportSvg
                      ? 'border-border hover:border-border-muted0'
                      : 'border-border-muted opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded flex items-center justify-center">
                      <span className="text-green-400 font-bold text-sm">SVG</span>
                    </div>
                    <div>
                      <p className="font-medium">SVG Vector</p>
                      <p className="text-sm text-foreground-tertiary">
                        {features.canExportSvg ? 'Scalable vector graphics' : 'Pro feature'}
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleExport('json')}
                  className="w-full p-4 text-left border border-border rounded-lg hover:border-border-muted0 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded flex items-center justify-center">
                      <span className="text-purple-400 font-mono text-sm">{'{}'}</span>
                    </div>
                    <div>
                      <p className="font-medium">JSON</p>
                      <p className="text-sm text-foreground-tertiary">For backup or sharing</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowExport(false)}
                  className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
